import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { fieldStyles } from "services/utils";
import MDButton from "components/MDButton";
import { createTicket } from "services/api";
import { useTranslation } from "react-i18next";

const ticketTypes = ["BUG", "FEATURE_REQUEST"];
const severities = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

function TicketModal({ open, onClose, currentUser, setTickets }) {
  const [form, setForm] = useState({
    type: "BUG",
    subject: "",
    description: "",
    replicationSteps: "",
    userSeverity: "LOW",
    pageUrl: window.location.href,
  });
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const ticket = await createTicket(form);
      // Update tickets instantly in UI
      if (ticket !== "error") setTickets((prev) => [ticket, ...prev]);

      onClose();
    } catch (error) {
      console.error("Ticket creation failed", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForm({
        type: "BUG",
        subject: "",
        description: "",
        replicationSteps: "",
        userSeverity: "LOW",
        pageUrl: window.location.href,
      });
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("tickets.create")}</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label={t("tickets.type")}
              name="type"
              value={form.type}
              sx={fieldStyles}
              onChange={handleChange}
            >
              {ticketTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`tickets.${type}`)}{" "}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label={t("tickets.severity")}
              name="userSeverity"
              sx={fieldStyles}
              value={form.userSeverity}
              onChange={handleChange}
            >
              {severities.map((sev) => (
                <MenuItem key={sev} value={sev}>
                  {t(`tickets.${sev}`)}{" "}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("tickets.subject")}
              name="subject"
              value={form.subject}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t("tickets.desc")}
              name="description"
              value={form.description}
              onChange={handleChange}
            />
          </Grid>

          {form.type === "BUG" && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("tickets.repSteps")}
                name="replicationSteps"
                value={form.replicationSteps}
                onChange={handleChange}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      {loading ? (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <CircularProgress size={20} sx={{ color: "black" }} />
        </Box>
      ) : (
        <DialogActions>
          <MDButton onClick={onClose}>{t("common.cancel")}</MDButton>
          <MDButton
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={!form.subject || !form.description}
          >
            {t("common.submit")}
          </MDButton>
        </DialogActions>
      )}
    </Dialog>
  );
}

TicketModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setTickets: PropTypes.func.isRequired,

  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string, // if UUID
      PropTypes.number, // fallback if still numeric
    ]).isRequired,
    employeeName: PropTypes.string,
    email: PropTypes.string,
  }).isRequired,
};

export default TicketModal;
